import { useNavigate } from 'react-router-dom';

// Tipos de erro possíveis
export enum ErrorCode {
  EXPIRED_LINK = 'EXPIRED_LINK',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_POST = 'INVALID_POST',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  DATABASE_ERROR = 'DATABASE_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  NOT_FOUND = 'NOT_FOUND',
  UNKNOWN = 'UNKNOWN'
}

interface ErrorDetails {
  code: ErrorCode;
  message: string;
  errorId?: string;
  details?: string;
}

interface ErrorPageProps {
  error: ErrorDetails;
}

const ERROR_MESSAGES = {
  [ErrorCode.EXPIRED_LINK]: {
    title: 'Ops, você chegou um pouquinho tarde!',
    description: 'Parece que você está acessando depois de 24h que postamos... Volte aqui com o link da próxima edição!'
  },
  [ErrorCode.INVALID_EMAIL]: {
    title: 'E-mail inválido',
    description: 'O e-mail fornecido não é válido. Por favor, verifique e tente novamente.'
  },
  [ErrorCode.INVALID_POST]: {
    title: 'Post inválido',
    description: 'Não foi possível encontrar o post especificado! Confira se você está acessando direto do e-mail.'
  },
  [ErrorCode.NETWORK_ERROR]: {
    title: 'Erro de conexão',
    description: 'Houve um problema com sua conexão. Por favor, verifique sua internet e tente novamente.'
  },
  [ErrorCode.SERVER_ERROR]: {
    title: 'Erro no servidor',
    description: 'Estamos com problemas técnicos. Nossa equipe já foi notificada e está trabalhando na solução.'
  },
  [ErrorCode.DATABASE_ERROR]: {
    title: 'Erro no banco de dados',
    description: 'Houve um problema ao acessar seus dados. Por favor, tente novamente em alguns instantes.'
  },
  [ErrorCode.UNAUTHORIZED]: {
    title: 'Acesso não autorizado',
    description: 'Você não tem permissão para acessar esta página.'
  },
  [ErrorCode.NOT_FOUND]: {
    title: 'Página não encontrada',
    description: 'O conteúdo que você está procurando não existe ou foi movido.'
  },
  [ErrorCode.UNKNOWN]: {
    title: 'Ops, algo deu errado!',
    description: 'Encontramos um erro inesperado. Por favor, tente novamente mais tarde.'
  }
};

export const ERROR_ICONS = {
  [ErrorCode.EXPIRED_LINK]: '⏰',
  [ErrorCode.INVALID_EMAIL]: '✉️',
  [ErrorCode.INVALID_POST]: '📝',
  [ErrorCode.NETWORK_ERROR]: '🌐',
  [ErrorCode.SERVER_ERROR]: '🔧',
  [ErrorCode.DATABASE_ERROR]: '💾',
  [ErrorCode.UNAUTHORIZED]: '🔒',
  [ErrorCode.NOT_FOUND]: '🔍',
  [ErrorCode.UNKNOWN]: '❌'
};

export function ErrorPage({ error }: ErrorPageProps) {
  const navigate = useNavigate();
  const errorMessage = ERROR_MESSAGES[error.code] || ERROR_MESSAGES[ErrorCode.UNKNOWN];
  const errorIcon = ERROR_ICONS[error.code] || ERROR_ICONS[ErrorCode.UNKNOWN];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="max-w-md w-full mx-4 p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-sm text-center">
        <div className="text-5xl mb-4">{errorIcon}</div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {errorMessage.title}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {errorMessage.description}
        </p>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => navigate('/FAQ')}
            className="inline-block px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Ver Soluções
          </button>
        </div>

        <div className="mt-6">
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Acredita que é um erro? Entre em contato <a href="https://app.thenewscc.com.br/support?newsletter=thenews" className="text-[#FF3366]">aqui</a>
          </p>
          {error.errorId && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              ID do erro: {error.errorId}
            </p>
          )}
        </div>
      </div>
    </div>
  );
} 